import { Row, Col, Form, Input, Select, Descriptions, Table, Typography } from 'antd';
import { FC, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FORM_FIELD } from './Commercial.constants';
import { NAMESPACE } from '@/constants';
import { formatMoney } from '@/constants/currency';
import { StyledInputNumber } from './Commercial.styles';
import { useChannelOptions } from '../../hooks/useChannelOptions';
import { ChannelFormContext } from '../../ChannelForm.context';
import {
  CalculatorPackageRow,
  useGetCalculatorPackageByIdQuery,
  useGetCalculatorPackageListQuery
} from '@/api/calculatorPackages';
import { useGetRateCardOptionsQuery, useGetAdminKpisQuery } from '@/api';
import { FORM_FIELD as CHANNEL_FIELD } from '../Channel/Channel.constants';
import { withSelectOption } from '../../../../utils/normalizeChannelFormPayload';

const { Item } = Form;
const { Option } = Select;
const { Item: DescriptionItem } = Descriptions;
const { Text } = Typography;

const formatPercent = (value: number | undefined): string =>
  value == null || Number.isNaN(value) ? '—' : `${value.toFixed(2)}%`;

/**
 * What a package-priced line is called on the IO.
 *
 * Printed verbatim in the IO's product column, so the client reads it. This
 * used to list every package row as "Publisher - Format - 12.5%", which on a
 * CTV package put the whole inventory breakdown and its delivery split onto a
 * signed document. Both are ours: the client agrees to a channel, a format and
 * a rate, and how delivery is divided between publishers is a trading decision
 * that can change within the same agreement.
 *
 * Formats are named; publishers and percentages are not.
 */
const formatPackageRowsForProduct = (
  rows: Array<{ publisher?: string; format?: string; deliveryPercentage?: number }>,
  channel?: string
): string => {
  const formats = Array.from(
    new Set((rows ?? []).map((r) => r?.format?.trim()).filter(Boolean))
  ) as string[];
  const name = channel?.trim();
  if (formats.length && name) return `${name} - ${formats.join(', ')}`;
  if (formats.length) return formats.join(', ');
  return name ?? '';
};

const Commercial: FC = () => {
  const context = useContext(ChannelFormContext);
  /** Opened on an existing line's values (edit or duplicate), rather than one being built. */
  const isEditing = !!context?.isPrefilled;
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);
  const { channel: channelFromContext } = useChannelOptions();
  const form = Form.useFormInstance();
  const channel = (Form.useWatch('channel', form) ?? channelFromContext) as string | undefined;
  const watchedRateSource = Form.useWatch(FORM_FIELD.RATE_SOURCE, form) as
    | 'MANUAL'
    | 'PACKAGE'
    | 'TECH_FEE'
    | undefined;
  const rateSource = (watchedRateSource ?? (context as any).rateSource ?? 'MANUAL') as
    | 'MANUAL'
    | 'PACKAGE'
    | 'TECH_FEE';
  const isPackageMode = rateSource === 'PACKAGE';
  const isTechFeeMode = rateSource === 'TECH_FEE';
  const isRateCardMode = rateSource === 'MANUAL';
  const selectedPackageId = Form.useWatch(FORM_FIELD.PACKAGE_ID, form) as string | undefined;
  const selectedProduct = Form.useWatch('product', form) as string | undefined;
  const selectedKpi = Form.useWatch(CHANNEL_FIELD.KPI, form) as string | undefined;
  const resolvedBenchmark = Form.useWatch('benchmark', form) as number | undefined;
  const sellRate = Form.useWatch(FORM_FIELD.SELL_RATE, form) as number | undefined;
  const selectedCurrency = Form.useWatch('currency', form) as string | undefined;

  const { data: selectedChannelRateCardOptions } = useGetRateCardOptionsQuery(
    {
      channel: channel ? [channel] : [],
      ...(selectedProduct ? { product: [selectedProduct] } : {}),
      showDraft: true
    },
    { skip: !channel || !isRateCardMode }
  );

  const { data: productKpiOptions } = useGetRateCardOptionsQuery(
    {
      channel: channel ? [channel] : [],
      product: selectedProduct ? [selectedProduct] : [],
      showDraft: true
    },
    { skip: !channel || !selectedProduct || !isRateCardMode }
  );

  const rateCardKpiOptions = useMemo(() => {
    const raw = productKpiOptions?.kpis ?? selectedChannelRateCardOptions?.kpis ?? [];
    const fromApi = Array.from(new Set(raw.filter(Boolean))).sort() as string[];
    return withSelectOption(fromApi, selectedKpi);
  }, [productKpiOptions?.kpis, selectedChannelRateCardOptions?.kpis, selectedKpi]);

  const productOptions = useMemo(() => {
    const fromApi = ((selectedChannelRateCardOptions as any)?.products ?? []) as string[];
    return withSelectOption(fromApi, selectedProduct);
  }, [selectedChannelRateCardOptions, selectedProduct]);

  const { data: packageListData, isFetching: isPackagesFetching } = useGetCalculatorPackageListQuery(
    {
      page: 1,
      limit: 50,
      ...(channel ? { channel } : {})
    },
    { skip: !isPackageMode }
  );

  const { data: adminKpisData, isLoading: areAdminKpisLoading } = useGetAdminKpisQuery(
    { includeInactive: true },
    { skip: !isPackageMode && !isTechFeeMode }
  );

  const packageKpiOptions = useMemo(() => {
    const raw = (adminKpisData?.kpis ?? []).map((k) => k.kpi).filter(Boolean);
    return Array.from(new Set(raw)).sort();
  }, [adminKpisData?.kpis]);

  const { data: packageById } = useGetCalculatorPackageByIdQuery(
    selectedPackageId ?? '',
    { skip: !isPackageMode || !selectedPackageId }
  );

  const selectedPackage = useMemo(() => {
    if (packageById) return packageById;
    if (!selectedPackageId) return undefined;
    return (packageListData?.data ?? []).find((p) => p._id === selectedPackageId);
  }, [packageById, packageListData?.data, selectedPackageId]);

  const packageRowsColumns = useMemo(
    () => [
      { title: 'Country', dataIndex: 'country', key: 'country' },
      { title: 'Channel', dataIndex: 'channel', key: 'channel' },
      { title: 'Publisher', dataIndex: 'publisher', key: 'publisher' },
      { title: 'Format', dataIndex: 'format', key: 'format' },
      { title: 'Delivery %', dataIndex: 'deliveryPercentage', key: 'deliveryPercentage' },
      { title: 'Currency', dataIndex: 'rowCurrency', key: 'rowCurrency' },
      {
        title: 'Buy CPM',
        dataIndex: 'buyCPM',
        key: 'buyCPM',
        render: (v: number, row: CalculatorPackageRow) => formatMoney(v, (row as any)?.rowCurrency)
      },
      { title: 'DSP fee %', dataIndex: 'dspFeePercentage', key: 'dspFeePercentage' }
    ],
    []
  );

  const packageOptions = useMemo(
    () =>
      (packageListData?.data ?? []).map((pkg) => ({
        value: pkg._id,
        label: `${pkg.name}${pkg.sellCpm != null && pkg.currency ? ` — Sell CPM: ${pkg.sellCpm} ${pkg.currency}` : ''}`
      })),
    [packageListData]
  );

  useEffect(() => {
    /* Not while editing an existing line.
     *
     * This repopulates product, formats and sell rate from the package, and it
     * re-runs whenever the package data settles — so on an existing line it
     * overwrote whatever had just been typed, which reads as the form
     * refusing to be edited. A package defines those fields when a line is
     * first built; afterwards the line's own stored values are the truth.
     *
     * The sibling Channel step already guards the same way. */
    if (isEditing) return;
    if (!isPackageMode || !selectedPackageId) return;
    const pkg = selectedPackage ?? (packageListData?.data ?? []).find((p) => p._id === selectedPackageId);
    if (!pkg) return;
    const updates: Record<string, unknown> = {};
    updates.product =
      (pkg as any)?.rows?.length > 0
        ? formatPackageRowsForProduct((pkg as any).rows, channel)
        : pkg.name ?? '';
    const rawFormat = (pkg as any)?.format as string | undefined;
    const formats = rawFormat
      ? rawFormat
          .split(',')
          .map((f) => f.trim())
          .filter(Boolean)
      : (pkg as any)?.rows?.map((r: any) => r?.format).filter(Boolean) ?? [];
    updates.formats = Array.isArray(formats) && formats.length > 0 ? [...new Set(formats)] : [];
    if (pkg.sellCpm != null && Number.isFinite(Number(pkg.sellCpm))) {
      updates[FORM_FIELD.SELL_RATE] = Number(pkg.sellCpm);
    }
    form.setFieldsValue(updates);
  }, [isEditing, isPackageMode, selectedPackageId, selectedPackage, packageListData, form]);

  useEffect(() => {
    if (!isRateCardMode || !selectedProduct) return;
    const opts = productKpiOptions as { buyingMetrics?: string[] | Record<string, string> } | undefined;
    const metrics = opts?.buyingMetrics;
    const firstMetric = metrics && (Array.isArray(metrics) ? metrics[0] : Object.values(metrics ?? {})[0]);
    if (!firstMetric) return;
    const currentBuyingMetric = form.getFieldValue(FORM_FIELD.BUYING_METRIC);
    if (currentBuyingMetric) return;
    form.setFieldValue(FORM_FIELD.BUYING_METRIC, firstMetric);
  }, [isRateCardMode, selectedProduct, productKpiOptions, form]);

  return (
    <Row gutter={20}>
      <Col span={24}>
        <Form.Item name={FORM_FIELD.RATE_SOURCE} hidden>
          <Input type="hidden" />
        </Form.Item>
        <div style={{ display: isPackageMode ? 'block' : 'none' }}>
          <Item
            rules={[{ required: isPackageMode, message: t('requiredFieldMessage', { ns: NAMESPACE.COMMON }) }]}
            name={FORM_FIELD.PACKAGE_ID}
            label={t('package')}
          >
            <Select
              size="large"
              showSearch
              optionFilterProp="label"
              loading={isPackagesFetching}
              placeholder={t('selectPackage')}
              options={packageOptions}
            />
          </Item>
          {selectedPackage && (
            <div style={{ marginTop: 12 }}>
              <Descriptions size="small" column={2} bordered>
                <DescriptionItem label="Package name">{selectedPackage.name}</DescriptionItem>
                <DescriptionItem label="Sell CPM">
                  {selectedPackage.sellCpm != null
                    ? formatMoney(selectedPackage.sellCpm, selectedPackage.currency)
                    : '—'}
                </DescriptionItem>
                <DescriptionItem label={t('blendedGrossMarginPercent', { ns: NAMESPACE.COMMON })}>
                  {formatPercent(selectedPackage.blendedMarginPercent)}
                </DescriptionItem>
                <DescriptionItem label={t('blendedGrossProfit', { ns: NAMESPACE.COMMON })}>
                  {formatMoney(selectedPackage.blendedProfit, selectedPackage.currency)}
                </DescriptionItem>
                <DescriptionItem label="Publishers">
                  {new Set(
                    (selectedPackage.rows ?? []).map((r: any) => r?.publisher).filter(Boolean)
                  ).size}
                </DescriptionItem>
              </Descriptions>
              <div style={{ marginTop: 12 }}>
                <Table<CalculatorPackageRow>
                  size="small"
                  columns={packageRowsColumns as any}
                  dataSource={(selectedPackage.rows ?? []).map((r, i) => ({ ...r, key: `${i}` } as any))}
                  pagination={false}
                  scroll={{ x: true }}
                />
              </div>
            </div>
          )}
        </div>
        <div style={{ display: isTechFeeMode ? 'block' : 'none' }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            {t('techFeeCommercialHint')}
          </Text>
        </div>
        <div style={{ display: isRateCardMode ? 'block' : 'none' }}>
          <Item
            rules={[{ required: isRateCardMode, message: t('requiredFieldMessage', { ns: NAMESPACE.COMMON }) }]}
            name="product"
            label={t('product')}
          >
            <Select
              size="large"
              placeholder={t('selectProduct')}
              disabled={!channel}
              loading={!selectedChannelRateCardOptions}
              onChange={() =>
                form.setFieldsValue({
                  [FORM_FIELD.SELL_RATE]: undefined,
                  [FORM_FIELD.BUYING_METRIC]: undefined,
                  [CHANNEL_FIELD.KPI]: undefined,
                  benchmark: undefined,
                  formats: undefined
                })
              }
            >
              {productOptions.map((p: string) => (
                <Option key={p} value={p}>
                  {p}
                </Option>
              ))}
            </Select>
          </Item>
        </div>
        <div style={{ display: isTechFeeMode ? 'none' : 'block' }}>
          <Item
            rules={[
              {
                required: !isTechFeeMode,
                message: t('requiredFieldMessage', { ns: NAMESPACE.COMMON })
              }
            ]}
            name={CHANNEL_FIELD.KPI}
            label={isPackageMode ? t('targetingKPI') : t('kpi')}
          >
            {isPackageMode ? (
              <Select loading={areAdminKpisLoading} size="large" placeholder={t('selectTargetingKPI')}>
                {packageKpiOptions.map((value) => (
                  <Option key={value} value={value}>
                    {value}
                  </Option>
                ))}
              </Select>
            ) : (
              <Select
                size="large"
                placeholder={t('selectTargetingKPI')}
                disabled={!selectedProduct}
                loading={!productKpiOptions && !selectedChannelRateCardOptions}
                onChange={() => form.setFieldsValue({ formats: undefined, benchmark: undefined })}
              >
                {rateCardKpiOptions.map((kpiOption) => (
                  <Option key={kpiOption} value={kpiOption}>
                    {kpiOption}
                  </Option>
                ))}
              </Select>
            )}
          </Item>
        </div>
        {isTechFeeMode && (
          <Form.Item name={CHANNEL_FIELD.KPI} hidden initialValue="N/A">
            <Input type="hidden" />
          </Form.Item>
        )}
        <div style={{ display: isRateCardMode ? 'block' : 'none' }}>
          {selectedKpi && (
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              {t('rateCardBenchmark')}:{' '}
              {resolvedBenchmark != null ? formatPercent(resolvedBenchmark) : t('noRateCardBenchmark')}
            </Text>
          )}
          {(context as any).hasBenchmarkConflict && (
            <Text type="danger" style={{ display: 'block', marginBottom: 16 }}>
              {t('rateCardBenchmarkConflict')}
            </Text>
          )}
          <Item
            rules={[{ required: isRateCardMode, message: t('requiredFieldMessage', { ns: NAMESPACE.COMMON }) }]}
            name={FORM_FIELD.BUYING_METRIC}
            label={t('buyingMetric')}
          >
            <Select
              size="large"
              placeholder={t('selectBuyingMetric')}
              disabled
              loading={!selectedChannelRateCardOptions}
            >
              {selectedProduct &&
                (() => {
                  const opts = selectedChannelRateCardOptions as any;
                  const metrics = opts?.buyingMetrics;
                  const vals = metrics
                    ? Array.isArray(metrics)
                      ? metrics
                      : Object.values(metrics)
                    : [];
                  return vals.map((m: string) => (
                    <Option key={m} value={m}>
                      {m}
                    </Option>
                  ));
                })()}
            </Select>
          </Item>
          <Item
            rules={[
              { required: isRateCardMode, type: 'number', message: t('requiredFieldMessage', { ns: NAMESPACE.COMMON }) }
            ]}
            name={FORM_FIELD.SELL_RATE}
            label={t('sellRate')}
          >
            <StyledInputNumber
              size="large"
              placeholder={t('inputSellRate')}
              min={0}
              formatter={(val) =>
                formatMoney(typeof val === 'string' ? Number(val) : (val as number), selectedCurrency)
              }
              parser={(val) => {
                const n = Number((val ?? '').toString().replace(/[^0-9.-]/g, ''));
                return Number.isFinite(n) ? n : (undefined as any);
              }}
            />
          </Item>
        </div>
      </Col>
    </Row>
  );
};

export default Commercial;
